import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { setSession } from './utils';
import { mutate } from 'swr';
import { logEvent } from 'src/utils/logger';
import { redirectToMainApp, replaceUrlWithoutParam } from 'src/utils/redirect';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

// Helper function to extract token from URL
const extractTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token');
};

// Extract redirection link from the URL
const extractRedirectionLinkFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('redirectingFrom');
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const urlToken = extractTokenFromUrl();
      const storedToken = localStorage.getItem(STORAGE_KEY);
      const accessToken = urlToken || storedToken;

      if (accessToken) {
        // If token came from URL, remove it from URL and store in localStorage
        if (urlToken) {
          replaceUrlWithoutParam('token');
          setSession(urlToken);
        } else {
          setSession(accessToken);
        }
        const redirectionLink = extractRedirectionLinkFromUrl();

        if (redirectionLink) {
          localStorage.setItem('redirectingFrom', redirectionLink);
        }

        const response = await axios.get(endpoints.auth.me);

        // Handle both response shapes
        let userData;
        let token;

        if (response.data && response.data.data) {
          // Shape 1
          userData = response.data.data;
          token = userData.token;
        } else if (response.data && response.data._id) {
          // Shape 2
          userData = response.data;
          token = response.data.token;
        }

        // Store token if present
        if (token) {
          setSession(token);
        }

        dispatch({
          type: 'INITIAL',
          payload: {
            user: userData,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
        redirectToMainApp(); // Redirect if no token found
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
      redirectToMainApp(); // Redirect on initialization error
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const apiData = { email, password };
    try {
      const response = await axios.post(endpoints.auth.login, apiData);

      // Handle both response shapes
      let data;
      let token;

      if (response.data && response.data.data) {
        // Shape 1
        data = response.data.data;
        token = data.token;
      } else if (response.data && response.data._id) {
        // Shape 2
        data = response.data;
        token = data.token;
      }

      if (!token) throw new Error('Login failed - no token received');

      setSession(token);

      const status = data?.status;
      const makeData = {
        ...data,
        permissions: data?.user?.permissions,
      };

      const userData = data?.user;

      if (!userData) throw new Error('User data missing after login');

      logEvent('login', {
        user_id: userData._id,
        module_type: 'lms',
        created_by: userData._id,
        meta: {
          email: userData?.email,
          firstName: userData?.firstName,
          lastName: userData?.lastName,
        },
      });

      if (status === 'active' || status === 'email sent') {
        dispatch({
          type: 'LOGIN',
          payload: {
            user: makeData,
          },
        });
      }

      redirectToMainApp(); // Redirect after successful login
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error; // Re-throw to let caller handle errors
    }
  }, []);
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(endpoints.auth.me);

      // Handle both response shapes
      let userData;
      let token;

      if (response.data && response.data.data) {
        // Shape 1
        userData = response.data.data;
        token = userData.token;
      } else if (response.data && response.data._id) {
        // Shape 2
        userData = response.data;
        token = response.data.token;
      }

      // Store token if present
      if (token) {
        setSession(token);
      }

      dispatch({
        type: 'INITIAL',
        payload: {
          user: userData,
        },
      });
      return userData;
    } catch (error) {
      console.error('User refresh error:', error);
      dispatch({ type: 'LOGOUT' }); // Ensure user is logged out on refresh failure
      redirectToMainApp(); // Redirect if user refresh fails
      return null;
    }
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const userData = {
      email,
      password,
      firstName,
      lastName,
    };

    try {
      const response = await axios.post(endpoints.auth.register, userData);

      const { accessToken, user } = response.data;

      if (!accessToken || !user) throw new Error('Registration failed - missing data');

      setSession(accessToken);

      dispatch({
        type: 'REGISTER',
        payload: {
          user,
        },
      });

      redirectToMainApp(); // Redirect after successful registration
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error; // Re-throw to let caller handle errors
    }
  }, []);

  //forgot password
  const forgotPassword = useCallback(async (email, companyId) => {
    const response = await axios.post(endpoints.auth.forgotPassword, { email, companyId });
    // console.log(response);
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    mutate(() => true, undefined, { revalidate: false });
    dispatch({
      type: 'LOGOUT',
    });
    redirectToMainApp(); // Redirect to main app after logout
  }, []);

  //new password
  const newPassword = useCallback(async (email, companyId, otpCode, password) => {
    const response = await axios.post(endpoints.auth.newPassword, {
      email,
      companyId,
      otpCode,
      password,
    });
    return response;
  }, []);

  //get company list
  const getCompanyList = useCallback(async (email) => {
    const response = await axios.post(endpoints.auth.getCompanyList, { email });
    return response;
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      refreshUser,
      forgotPassword,
      newPassword,
      getCompanyList,
    }),
    [
      login,
      logout,
      refreshUser,
      register,
      state.user,
      status,
      forgotPassword,
      newPassword,
      getCompanyList,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};